/**
 * noctlux.js — scroll reveal.
 * Session 24, 2026-08-30.
 *
 * Savor ships NO scroll animation. Measured: its entire animation surface is
 * four settings (page_transition_enabled, transition_to_main_product,
 * add_to_cart_animation, card_hover_effect). Grepped every asset — no
 * IntersectionObserver reveal, no animation-timeline, no keyframes for it.
 * This is the one thing in the Noctlux layer that had to be built rather than
 * configured.
 *
 * The mechanism is Haven's, measured out of its own theme.js:
 *   - ONE observer per SECTION, never per element. Watching each element kills
 *     the stagger, because each would then fire on its own schedule.
 *   - rootMargin '0px 0px -100px 0px' — fires 100px AFTER the section enters,
 *     so nothing animates while still half off-screen.
 *   - stagger = index * 125ms, set as a custom property on each target.
 *   - unobserve after firing. Plays once per page load, never on scroll-back.
 *     This is most of why it reads as calm rather than busy.
 *
 * The CSS half is in assets/noctlux.css §2, including the
 * prefers-reduced-motion guard. See playbook/REF-haven-measured.md §1b.
 */
(() => {
  const STAGGER = 125; // [H] measured on 12 of 12 Haven home-page sections

  /**
   * Sections that must NEVER animate.
   *
   * ⭐ Haven does not animate its own main product section either — verified in
   *    reference/haven/haven_product.html: <section-product> carries no
   *    data-animate-elements-on-scroll, while the featured-collection, video
   *    hero and accordion panels below it all do. The buy area has to be there
   *    the instant the page paints; a customer arriving from a Shopping ad must
   *    not watch Add to cart fade in.
   */
  const SKIP = [
    '.shopify-section-group-header-group',
    '.shopify-section-group-footer-group',
  ].join(',');

  /**
   * Sections to skip based on what they CONTAIN.
   *
   * ⚠ `.product-information` — the buy area. Haven does not animate its own
   *   main product section either (verified: <section-product> carries no
   *   data-animate-elements-on-scroll while everything below it does). A
   *   customer arriving from a Shopping ad must not watch Add to cart fade in.
   *
   * ⚠ drawers and dialogs — the cart drawer is a .shopify-section too, and on
   *   the first build the ONLY animating element on the whole product page was
   *   <theme-drawer> inside it: a drawer whose contents faded in over 1.75s
   *   every time it opened. Chrome caught that; markup could not.
   *   The first fix keyed off `.shopify-section-cart-drawer-section`, which is
   *   the element's ID and not a class — so it silently kept matching nothing.
   *   Measured again, then keyed off the content instead.
   */
  const SKIP_INNER = [
    '.product-information',
    'theme-drawer',
    'dialog',
    'cart-drawer-component',
    // ⚠ Session 33, 2026-09-08. `product-recommendations` hydrates its own
    //   grid after load, and on the product page the reveal never fires for
    //   it: measured with the page rendering and the section fully in view
    //   (top 304, bottom 684, viewport 844) the section still read
    //   data-nx-reveal="false", so its heading and cards sat at opacity 0.01
    //   — invisible. Both cross-sell sections were dark bands.
    //   armWatch() re-stamps on mutation, so this is NOT the session-24 case
    //   of a wiped class; the IntersectionObserver itself never delivers for
    //   this section and the cause is not yet established.
    //   Skipping is the fallback, not the fix: a cross-sell strip does not
    //   need a reveal, and a visible strip beats an animated invisible one.
    //   The underlying observer bug is still open — see the handoff.
    '.product-recommendations',
  ].join(',');

  /**
   * Containers that should contribute their CHILDREN as individual targets
   * rather than animating as one block. These are the grids and headers —
   * exactly what Haven hand-marks with .animate on each card.
   */
  const EXPAND = [
    '.resource-list--grid',
    '.section-resource-list__header',
    '.custom-section-content',
    '.hero__content-wrapper',
  ].join(',');

  /** Wrappers that paint a background or hold no visible content. */
  const IGNORE = [
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'LINK',
    'NOSCRIPT',
  ];
  const IGNORE_CLASS = /(^|\s)(section-background|custom-section-background)(\s|$)/;

  const isVisibleCandidate = (el) =>
    el.nodeType === 1 && !IGNORE.includes(el.tagName) && !IGNORE_CLASS.test(el.className || '');

  /** The element inside a section that actually holds the laid-out content. */
  const contentRoot = (section) => section.querySelector('.section') || section;

  /**
   * Build the ordered target list for one section.
   * Direct children of the content root, with grids and header groups
   * replaced by their own children so each card animates on its own beat.
   */
  function targetsFor(section) {
    const root = contentRoot(section);
    const out = [];
    for (const child of root.children) {
      if (!isVisibleCandidate(child)) continue;
      const grid = child.matches(EXPAND) ? child : child.querySelector(EXPAND);
      if (grid && grid.children.length > 1) {
        for (const cell of grid.children) {
          if (isVisibleCandidate(cell)) out.push(cell);
        }
      } else {
        out.push(child);
      }
    }
    return out;
  }

  /** Apply (or re-apply) the animate class and per-element delay. */
  function stamp(section) {
    const targets = targetsFor(section);
    if (!targets.length) return false;
    // clear a previous pass before re-stamping — see armWatch() below
    for (const stale of section.querySelectorAll('.nx-animate')) {
      if (!targets.includes(stale)) {
        stale.classList.remove('nx-animate');
        stale.style.removeProperty('--nx-delay');
      }
    }
    targets.forEach((el, i) => {
      el.classList.add('nx-animate');
      el.style.setProperty('--nx-delay', `${i * STAGGER}ms`);
    });
    return true;
  }

  const observers = new WeakMap();

  function onIntersection(entries, observer) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const el = entry.target;
      if (el.getAttribute('data-nx-reveal') !== 'false') continue;
      el.setAttribute('data-nx-reveal', 'true');
      observer.unobserve(el); // [H] once per page load, never again
      const mo = observers.get(el);
      if (mo) {
        mo.disconnect();
        observers.delete(el);
      }
    }
  }

  /**
   * ⚠ Some Savor sections render their own children AFTER DOMContentLoaded —
   *   product-recommendations fetches its grid, and the paginated lists
   *   hydrate. Session 24 measured the first build: the recommendations grid
   *   was EMPTY when init ran, so the whole grid was stamped as one target,
   *   and the fetch then replaced its innerHTML and wiped the class. Result:
   *   six product cards with no animation and nothing in the markup to show
   *   it had gone wrong.
   *
   *   So watch each armed section for child changes and re-stamp, until it
   *   fires. class/style writes are attribute mutations, not childList ones,
   *   so this cannot feed itself.
   */
  function armWatch(section) {
    const mo = new MutationObserver(() => {
      if (section.getAttribute('data-nx-reveal') !== 'false') {
        mo.disconnect();
        observers.delete(section);
        return;
      }
      stamp(section);
    });
    mo.observe(section, { childList: true, subtree: true });
    observers.set(section, mo);
  }

  /**
   * ⚠ THE REVEAL FAILS OPEN NOW — safety net, added session 33, 2026-09-08.
   *
   * The IntersectionObserver has now been measured failing to deliver three
   * separate times, on three different sections: both product-recommendations
   * sections, and the back-to-collection link. Each time the section was
   * fully in view with the page painting, and each time data-nx-reveal stayed
   * "false", so the content sat at opacity 0.01 — invisible. The cause is
   * still not established; what IS established is the consequence, which is
   * that a customer silently loses whole sections of the page.
   *
   * An animation is a nicety. Content being visible is not. So: sweep on
   * scroll, and reveal anything already in view whatever the observer did.
   * When the observer works — the normal case — it fires first and this sweep
   * finds nothing to do, so the stagger is unchanged.
   */
  function armFailOpenSweep() {
    let queued = false;

    const sweep = () => {
      queued = false;
      const vh = innerHeight;
      let remaining = 0;
      for (const section of document.querySelectorAll('[data-nx-reveal="false"]')) {
        const r = section.getBoundingClientRect();
        if (r.top < vh - 100 && r.bottom > 0) {
          section.setAttribute('data-nx-reveal', 'true');
        } else {
          remaining++;
        }
      }
      // must repeat the capture flag or this removes nothing
      if (!remaining) document.removeEventListener('scroll', onScroll, { capture: true });
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      setTimeout(sweep, 100);
    };

    /* ⚠ NOT `addEventListener('scroll')` on window. Measured 8 Sept: this
       theme does not scroll the window at all — html and body are
       height:100vh/overflow:hidden and the real scroller is .page-wrapper
       (clientHeight 900, scrollHeight 2188). Scroll events do not bubble, so
       a window listener never hears that element and the sweep never ran.
       Capture phase on document catches a scroll from ANY element, so this
       works whatever the theme decides to scroll next. */
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });

    /* and a few timed passes, for whatever is already in view at load */
    setTimeout(sweep, 400);
    setTimeout(sweep, 1500);
    setTimeout(sweep, 3000);
  }

  function init(root = document) {
    const io = new IntersectionObserver(onIntersection, {
      rootMargin: '0px 0px -100px 0px', // [H] verbatim
    });

    for (const section of root.querySelectorAll('.shopify-section')) {
      if (section.matches(SKIP) || section.closest(SKIP)) continue;
      if (section.querySelector(SKIP_INNER)) continue;
      if (section.hasAttribute('data-nx-reveal')) continue;
      if (!stamp(section)) continue;

      section.setAttribute('data-nx-reveal', 'false');
      armWatch(section);
      io.observe(section);
    }
  }

  /**
   * The zoom dialog opens already magnified.
   *
   * Session 33, 2026-09-08. Measured: <drag-zoom-wrapper> carries an inline
   * `--drag-zoom-scale: 1.5`, so clicking a gallery image opened the lightbox
   * at 150% and the shopper saw the middle of the photo with no way to see it
   * whole. Owner called it out directly.
   *
   * It has to be JS. The 1.5 is an INLINE custom property, so a stylesheet
   * cannot beat it without !important — and !important would pin the scale at
   * 1 forever, killing pinch and double-tap zoom. Writing the inline value
   * back to 1 leaves the component free to change it afterwards, which is the
   * whole point: open fitted, magnify on purpose.
   *
   * setTimeout, not requestAnimationFrame: rAF does not run in a background
   * or unpainted tab, and this must survive that.
   */
  function fitZoomOnOpen() {
    const reset = () => {
      for (const w of document.querySelectorAll(
        '.dialog-zoomed-gallery .product-media__drag-zoom-wrapper'
      )) {
        if (w.style.getPropertyValue('--drag-zoom-scale') !== '1') {
          w.style.setProperty('--drag-zoom-scale', '1');
        }
      }
    };
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest('.product-media-container--zoomable, .product-media-container__zoom-button')) return;
      // the dialog is populated after the click resolves; two passes covers
      // both the immediate open and a slower hydration
      setTimeout(reset, 0);
      setTimeout(reset, 200);
    });
  }

  /**
   * §6. THE RATING BADGE HAS TO REACH THE REVIEWS.
   * Session 34, 2026-09-10.
   *
   * The Judge.me review widget moved out of the main section and now sits in
   * its own section between the grouped details and Completes the Room. The
   * small rating badge stays beside the title as the shortcut to it — Baymard's
   * point that a shopper who wants reviews first should not have to hunt.
   *
   * ⚠ It cannot be left to the anchor. Two measured facts fight it:
   *   §3a  this theme sets html,body { height:100vh; overflow:hidden }. The
   *        window does not scroll; .page-wrapper does. Anything that calls
   *        window.scrollTo — Judge.me's own handler included — moves nothing.
   *   §3b  scroll-behavior:smooth silently swallows programmatic scrolling.
   *
   * So: find the real scroller by walking up from the target, ask for smooth,
   * and verify. If the position has not moved after 500ms, force it. Fail
   * open, the same shape as armFailOpenSweep.
   */
  function reviewJump() {
    /* ⚠ MEASURED: Shopify prefixes a template section's id, so the element is
       #shopify-section-template--27930800455970__nx_reviews, never the bare
       #shopify-section-nx_reviews the first attempt looked for. Judge.me's own
       #judgeme_product_reviews sits inside it and is the better target when the
       widget has rendered; the section is the fallback while it is still
       hydrating. */
    const TARGETS = [
      '#judgeme_product_reviews',
      '[id$="__nx_reviews"]',
    ];
    const findTarget = () => {
      for (const sel of TARGETS) {
        const el = document.querySelector(sel);
        if (el) return el;
      }
      return null;
    };

    /* MEASURED TWICE, and the second reading corrected the first.
       On Sero the target landed 168px below the header line rather than at it,
       which looked like the arithmetic being wrong. It was not: .page-wrapper
       was already at its maximum scrollTop (2018 of 2018) because the reviews
       sit near the end of a draft page, and no scroller can go further than its
       own end. Recorded here so it is not "fixed" again later.

       scrollIntoView is used anyway, and it is the better call on its own
       merits: it finds and scrolls the correct ancestor itself, which is
       exactly what §3a's window.scrollTo cannot do on this theme. The landing
       is then verified at 600ms and 1400ms so a late-loading image above the
       fold cannot leave the shopper short. scroll-margin (css §15e) keeps the
       sticky header off the heading. */
    const landed = (el) => {
      const top = el.getBoundingClientRect().top;
      return top > -8 && top < 220;
    };

    const go = (target) => {
      try {
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } catch {
        target.scrollIntoView(true);
      }
      const settle = () => {
        if (!landed(target)) target.scrollIntoView({ block: 'start' });
      };
      setTimeout(settle, 600);
      setTimeout(settle, 1400);
    };

    // capture phase: Judge.me binds its own click on the badge, and its handler
    // scrolls the window, which does nothing here.
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest(
        '.jdgm-prev-badge, .jdgm-preview-badge, [data-nx-review-jump],' +
        ' a[href="#judgeme_product_reviews"], a[href$="#judgeme_product_reviews"]'
      )) return;
      const target = findTarget();
      if (!target) return;   // no reviews section on this template — leave it alone
      e.preventDefault();
      e.stopPropagation();
      go(target);
    }, { capture: true });
  }

  /**
   * §7. IN-PAGE ANCHOR LINKS HAVE TO SCROLL THE RIGHT THING TOO.
   *
   * Same root cause as §6, reached from a different direction. The homepage
   * hero CTA and the featured link both point at /#categories.
   *
   *   MEASURED on noctlux-build, 13 September 2026, 1440px: clicking the hero
   *   CTA updated location.hash to #categories and left .page-wrapper.scrollTop
   *   at 0. Nothing moved. base.css puts html and body at height:100dvh;
   *   overflow:hidden above 990px and makes .page-wrapper the scroller, and
   *   native fragment navigation scrolls the document — which here cannot
   *   move. Below 990px the document scrolls normally and anchors work
   *   unaided, which is why this only misbehaves on desktop.
   *
   * The remedy is §6's, for §6's reason: scrollIntoView locates and scrolls the
   * correct ancestor itself. scroll-margin on the target keeps the sticky
   * header off its heading.
   *
   * ⚠ The default is deliberately NOT prevented. Letting the browser perform
   * the fragment navigation keeps the hash, history and — importantly — the
   * :target pseudo-class working, which is the entire interaction model of the
   * shoppable-room markers. We only add the scroll the theme is missing.
   */
  /**
   * ⚠ MEASURED on the build theme, 1440px, 13 September 2026. Four ways of
   * scrolling to #categories, .page-wrapper.scrollTop sampled for 1.5s:
   *
   *   scrollIntoView({behavior:'smooth'})   0,0,0,0,0,0   ← never moves
   *   scrollIntoView({behavior:'instant'})  598           ← works
   *   pageWrapper.scrollTo({...'smooth'})   598           ← works
   *   pageWrapper.scrollTop = 598           598           ← works
   *
   * So this is NOT "smooth is broken" — §3b's note is narrower than it reads.
   * It is scrollIntoView SPECIFICALLY that the theme's
   * `.page-wrapper { scroll-behavior: smooth }` swallows. Scrolling the
   * container directly is fine and keeps the animation. Hence the arithmetic
   * below rather than the one-liner §6 uses.
   */
  const nxScrollParent = (el) => {
    let node = el.parentElement;
    while (node) {
      const oy = getComputedStyle(node).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const nxScrollTo = (target) => {
    const box = nxScrollParent(target);
    const isDoc = box === document.scrollingElement || box === document.documentElement;
    // honour the target's own scroll-margin so the sticky header clears it
    const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;

    const desired = () => {
      const top = target.getBoundingClientRect().top;
      const base = isDoc ? window.scrollY || 0 : box.scrollTop;
      const offset = isDoc ? 0 : box.getBoundingClientRect().top;
      return Math.max(0, base + top - offset - margin);
    };

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      box.scrollTo({ top: desired(), behavior: reduced ? 'instant' : 'smooth' });
    } catch {
      box.scrollTop = desired();
    }

    // §6's landing check, kept: a late-loading image above the fold can
    // otherwise leave the shopper short of the section they asked for.
    const settle = () => {
      const top = target.getBoundingClientRect().top;
      if (top < -8 || top > 220) {
        try {
          box.scrollTo({ top: desired(), behavior: 'instant' });
        } catch {
          box.scrollTop = desired();
        }
      }
    };
    setTimeout(settle, 700);
    setTimeout(settle, 1500);
  };

  const resolveHash = (hash) => {
    if (!hash || hash.length < 2) return null;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      id = hash.slice(1);
    }
    return document.getElementById(id);
  };

  const pageAnchors = () => {
    document.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const t = e.target;
      if (!(t instanceof Element)) return;
      const link = t.closest('a[href]');
      if (!link || link.target === '_blank') return;

      let url;
      try {
        url = new URL(link.href, location.href);
      } catch {
        return;
      }
      // Same document only. The query string is ignored on purpose: the
      // preview URL carries ?preview_theme_id and the links do not.
      if (url.pathname !== location.pathname || url.host !== location.host) return;

      const target = resolveHash(url.hash);
      if (!target) return;
      // Let the navigation happen, then scroll on the next tick.
      setTimeout(() => nxScrollTo(target), 0);
    });

    // Direct entry, a pasted link, or back/forward.
    window.addEventListener('hashchange', () => {
      const target = resolveHash(location.hash);
      if (target) nxScrollTo(target);
    });

    if (location.hash) {
      const target = resolveHash(location.hash);
      // wait for above-the-fold media to reserve its space first
      if (target) setTimeout(() => nxScrollTo(target), 250);
    }
  };

  const start = () => {
    init();
    armFailOpenSweep();
    fitZoomOnOpen();
    reviewJump();
    pageAnchors();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /**
   * The theme editor swaps section markup in place. Re-arm on those events, and
   * let the CSS escape hatch (.shopify-design-mode, noctlux.css §2) keep
   * everything visible while editing.
   */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const t = e.target;
      if (t instanceof Element) {
        t.removeAttribute('data-nx-reveal');
        init(t.parentElement || document);
      }
    });
  }
})();
